import { FormLink } from '@azoya-spa/ui'
import '@azoya-spa/ui/styles.css'
import { useSiteContent } from '../../hooks/useSiteContent'
import practicalInfoFallback from '../../api/fallback/practicalInfo.json'
import { frenchNumberToTelHref } from '../../utils/phone'
import { phoneClicked, planityExternalLinkClicked } from '../../analytics/events'
import './PlanityWidgetStatus.css'

// Les deux pages qui embarquent un module Planity — Réserver en ligne et
// Offrir — partagent ces états, à un mot près. Le mot est ici : le reste de
// l'anatomie est identique, et c'est ce qui les rend reconnaissables comme
// « le même écran » d'un cadre à l'autre.
const MOTS = {
  reservation: {
    chargementTitre: 'Le module de réservation s’ouvre.',
    chargementTexte: 'Vos soins et vos créneaux sont récupérés auprès de Planity. Encore un instant.',
    surtitre: 'Réservation en ligne indisponible',
    titre: 'La réservation en ligne n’est pas disponible pour le moment.',
    consequence:
      'Nos créneaux, eux, sont ouverts : appelez l’institut, on vous trouve une place et on la note tout de suite.',
    planity: 'Réserver sur la page Planity de l’institut',
  },
  cadeau: {
    chargementTitre: 'Le module de carte cadeau s’ouvre.',
    chargementTexte: 'Les montants et les formules sont récupérés auprès de Planity. Encore un instant.',
    surtitre: 'Achat en ligne indisponible',
    titre: 'L’achat en ligne n’est pas disponible pour le moment.',
    consequence:
      'La carte cadeau, elle, est disponible : appelez l’institut, on la prépare avec vous et on vous l’envoie.',
    planity: 'Acheter sur la page Planity de l’institut',
  },
}

// Affiché pendant l'attente des réglages de réservation (cf.
// useBookingSettings, statut 'loading'). Trois points qui respirent plutôt
// qu'un rond qui tourne : une attente de deux secondes n'est pas une panne, et
// la traiter comme telle inquiéterait pour rien. Pas de sortie non plus — il
// n'y a rien à fuir.
export function PlanityWidgetLoading({ contexte = 'reservation' }) {
  const mots = MOTS[contexte] ?? MOTS.reservation

  return (
    <div className="planity-widget-etat" role="status" aria-live="polite">
      <div className="planity-widget-points" aria-hidden="true">
        <span className="planity-widget-point" />
        <span className="planity-widget-point" />
        <span className="planity-widget-point" />
      </div>
      <div className="planity-widget-attente-titre">{mots.chargementTitre}</div>
      <p className="planity-widget-attente-texte">{mots.chargementTexte}</p>
    </div>
  )
}

// Affiché à la place du module quand les réglages n'ont pas pu être chargés
// (cf. useBookingSettings, statut 'unavailable' : requête en échec, ou
// authentiquement bloquée au-delà du coupe-circuit). Le module n'est alors
// jamais injecté — voir le rapport de tâche 14, Finding 2 : une clé de secours
// potentiellement révoquée serait pire qu'une absence de module.
//
// C'est l'écran qui compte : la visiteuse était à un geste d'être cliente. Le
// téléphone est donc le plus gros élément du panneau, pas une note de bas de
// page — et les horaires sont juste dessous, pour qu'elle sache si ça décroche
// maintenant. Le numéro comme les horaires viennent des informations pratiques
// déjà exposées publiquement par l'API, même source que le pied de page.
//
// Les deux sorties sont mesurées (tâche 16, revue finale) bien que ce composant
// soit monté à la fois par ReserverEnLigne.jsx et par PlanityGift.jsx (la page
// /offrir). Cela ne contredit pas la décision 7 du cahier des charges
// (« la page /offrir elle-même n’émet rien de plus que son $pageview ») :
// cette décision porte sur `gift_card_opened` et sur le risque de compter deux
// fois un accès à la carte cadeau. Un clic sur le téléphone ou un départ vers
// planity.com ne sont pas des accès à la carte cadeau — ce sont les mêmes
// événements que partout ailleurs sur le site, et les compter ici est
// cohérent avec le reste de la mesure.
//
// Conséquence assumée : `phone_clicked` n’a pas de propriété `emplacement`.
// Les appels depuis le pied de page, l’impasse de recherche des Services, le
// bandeau de /reserver-en-ligne et ce repli Planity sont donc confondus dans la
// mesure. C’est un choix, pas un oubli.
export function PlanityWidgetUnavailable({ planityUrl, contexte = 'reservation' }) {
  const info = useSiteContent('practical-info', practicalInfoFallback)
  const mots = MOTS[contexte] ?? MOTS.reservation

  return (
    <div className="planity-widget-etat" role="status" aria-live="polite">
      <div className="planity-widget-entete">
        <span className="planity-widget-pastille" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e86f51" strokeWidth="1.7">
            <circle cx="12" cy="12" r="8" strokeDasharray="3 3.4" />
          </svg>
        </span>
        <span className="planity-widget-surtitre">{mots.surtitre}</span>
      </div>

      <h2 className="planity-widget-titre">{mots.titre}</h2>
      <p className="planity-widget-consequence">{mots.consequence}</p>

      <a
        className="planity-widget-tel"
        href={frenchNumberToTelHref(info.phone)}
        onClick={() => phoneClicked()}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" aria-hidden="true">
          <path d="M4 4h4l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 3 5a1 1 0 0 1 1-1z" />
        </svg>
        Appeler le {info.phone}
      </a>

      {info.openingLines?.length > 0 && (
        <div className="planity-widget-horaires">
          {info.openingLines.map((ligne) => (
            <span key={ligne}>{ligne}</span>
          ))}
        </div>
      )}

      {/* La maquette demandait ici un `Button variant="light"`. Cette variante
          est documentée pour les fonds sombres — elle est blanche, sans
          bordure — et le panneau qui l'accueille est blanc : le bouton y
          disparaissait, ne laissant qu'un texte gras qui ne se donnait pas
          pour cliquable. `FormLink` est l'action discrète du système, tenue
          pour lisible sur blanc (5,21:1 au survol) et à 44 px de cible. La
          hiérarchie voulue est conservée : le téléphone reste le geste
          principal, celui-ci la seconde chance. */}
      <div className="planity-widget-repli">
        <FormLink
          as="a"
          href={planityUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => planityExternalLinkClicked()}
        >
          {mots.planity}
        </FormLink>
      </div>
    </div>
  )
}
